import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers.ts';
import IResponse, { IPagination } from '../../interfaces/response.ts';
import { useDebounce } from 'use-debounce';
import { filterList } from '../../utils/array.ts';
import { isNull } from 'lodash';
import axios from 'axios';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { Box, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BaranDataGrid from '../common/BaranDataGrid.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { exportToExcel } from '../../utils/file.ts';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface IPackages {
	id?: number;
	title?: string;
	price?: number;
	media?: string;
}
export default function Packages() {
	const { t } = useTranslation();
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFetchedData] = useState<IPackages[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const { type } = useParams();

	const headerClassNames = 'text-xs bg-background-paper';
	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};
	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'companies_list', t });
	};

	const navigate = useNavigate();

	const properties: (keyof IPackages)[] = ['title', 'media', 'price'];
	const filteredRows = filterList(fetchedData, properties, searchTerm);
	const handleClickOpen = (id: number) => {
		navigate(`/admin/studio/package/edit/${id}`);
	};

	const handleClickDelete = (id: number) => {
		if (!isNull(id)) {
			axios({
				url: `/admin/studio-packages/${id}`,
				method: 'DELETE',
				loading: true,
			}).then(() => {
				getServer();
			});
		}
	};

	const handleNew = () => {
		navigate('/admin/studio/package/new');
	};
	const handleViewTicket = (id: number) => () => {
		navigate(`/support/view/${id}`);
	};

	const columns: GridColDef<IPackages>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'title',
			headerName: t('title'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'category_title',
			headerName: t('category'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'media_title',
			headerName: t('platform'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'quality_title',
			headerName: t('quality'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'price',
			headerName: t('price'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: (params: any) => {
				return (
					<>
						<IconButton onClick={() => handleClickOpen(params?.row?.id)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton aria-label="eye" onClick={handleViewTicket(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={() => handleClickDelete(params?.row?.id)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	const getServer = () => {
		setLoading(true);
		let srch = JSON.stringify([
			{
				field: 'type',
				value: search,
			},
		]);
		axios<IResponse<IPackages[]>>({
			url: '/admin/studio-packages',
			method: 'GET',
			params: {
				page: page,
				limit: pageSize,
				search: srch,
				type: type,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(
						res.data.data.map(payment => ({
							...payment,
						})),
					);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize, type]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);

	return (
		<Box className="w-full">
			<BaranDataGrid<IPackages> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('export_excel')} onClick2={handleNew} actionButtonLable2={t('create_new_package')} searchableFields={properties} isCreateBtn2={true} />
		</Box>
	);
}
